/**
 * 🎯 formatItemHoraire — Helper centralisé de libellés d'items cédulés (v13.60.2).
 *
 * Objectif : produire un libellé LISIBLE et COHÉRENT pour tout item affiché dans
 * l'horaire ou la validation heures (service call, route lavage, BT chantier,
 * BT interne, horaire bureau, congé, RDV).
 *
 * Contrat :
 *   - Jamais d'ID technique visible (SEED_*, uuid, hash, etc.).
 *   - Jamais de `[object Object]` ni `undefined`.
 *   - Format cible unique : "Type — Nom du client" (ou variante contextuelle).
 *
 * Signature :
 *   formatItemHoraire(item) -> string
 *
 * Sources acceptées pour `item` :
 *   - Punch record (avec `type_punch`, `contrat_nom`, `client_nom`, `service_call_*`)
 *   - Schedule event (avec `event_type`, `client_nom`, `nom_dossier`, `est_interne`, ...)
 *   - Bon de travail enrichi (avec `_is_service_call`, `est_interne`, `client_nom`, ...)
 *
 * Robustesse : chaque champ est optionnel, chaîne vide traitée comme absente.
 */

// ─────────────────────────────────────────────────────────────
// Helpers internes — extraction résiliente
// ─────────────────────────────────────────────────────────────

function _s(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v.trim();
  return '';
}

/**
 * Extrait le nom du client depuis plusieurs structures possibles.
 * Priorité : client_nom > nom_client > client.nom > dossier.client_nom > bon.client_nom.
 * Retourne '' si aucune source valide.
 */
function extractClientName(item) {
  if (!item || typeof item !== 'object') return '';
  const candidates = [
    item.client_nom,
    item.nom_client,
    item.client && item.client.nom,
    item.dossier && item.dossier.client_nom,
    item.bon && item.bon.client_nom,
    item.nom_client_lavage, // v13.60.2 backend enrichment
  ];
  for (const c of candidates) {
    const s = _s(c);
    if (s) return s;
  }
  return '';
}

/**
 * Détecte le type sémantique de l'item.
 * Retourne un tag interne : 'service_call' | 'lavage' | 'bt_interne' |
 * 'bureau' | 'conge' | 'rendez_vous' | 'bt_chantier'.
 */
function detectType(item) {
  if (!item || typeof item !== 'object') return 'bt_chantier';
  // Priorité 1 : flags/type explicites
  if (item.type_punch === 'service_call' || item._is_service_call === true) {
    return 'service_call';
  }
  if (
    item.type_punch === 'lavage'
    || item.event_type === 'lavage'
    || item.route_lavage_id
    || item.route_id
  ) {
    return 'lavage';
  }
  if (
    item.est_interne === true
    || item.event_type === 'bt_interne'
    || item.type_travaux === 'interne'
  ) {
    return 'bt_interne';
  }
  if (
    item.event_type === 'bureau'
    || item.event_type === 'horaire'
    || item.type_punch === 'admin'
  ) {
    return 'bureau';
  }
  if (item.event_type === 'conge' || item.type_punch === 'ferie_cnesst') {
    return 'conge';
  }
  if (item.event_type === 'rendez_vous') {
    return 'rendez_vous';
  }
  return 'bt_chantier';
}

/**
 * Reconnaît si une chaîne semble être un ID technique (à masquer).
 * Ex: SEED_V13_60_1_route, uuid, service_call_xxxx, [object Object].
 */
function looksLikeTechnicalId(s) {
  if (!s) return false;
  if (s === '[object Object]' || s === 'undefined' || s === 'null') return true;
  // uuid v4
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return true;
  // Préfixes seed / IDs techniques classiques
  if (/^(SEED_|TEST_|TC\d_|service_call_|route_lavage_|bt_)/i.test(s)) return true;
  return false;
}

/**
 * Nettoie une valeur candidate en éliminant les IDs techniques.
 */
function cleanCandidate(s) {
  const v = _s(s);
  if (!v) return '';
  if (looksLikeTechnicalId(v)) return '';
  return v;
}

// ─────────────────────────────────────────────────────────────
// API publique
// ─────────────────────────────────────────────────────────────

/**
 * Formate un item en libellé lisible.
 * @param {object} item — punch, schedule event, ou BT enrichi.
 * @returns {string} — libellé prêt à afficher.
 */
function formatItemHoraire(item) {
  // Guard input : null / undefined / non-objet → fallback safe
  if (!item || typeof item !== 'object') return 'Chantier';
  const type = detectType(item);
  const clientNameRaw = extractClientName(item);
  const clientName = cleanCandidate(clientNameRaw);

  switch (type) {
    case 'service_call': {
      // Pour SC : le nom client est la seule info fiable.
      // Fallback : chercher dans le contrat_nom (peut commencer par "SC: ...")
      if (clientName) return `Service call — ${clientName}`;
      const alt = cleanCandidate((item.contrat_nom || '').replace(/^SC:\s*/i, ''));
      if (alt) return `Service call — ${alt}`;
      return 'Service call — sans client';
    }

    case 'lavage': {
      // v13.60.2 : préfère le nom client réel enrichi backend (nom_client_lavage).
      // Sinon si nb_clients connu → format compact.
      // Sinon → fallback safe.
      const nb = parseInt(item.nb_clients, 10);
      // Priorité 1 : nom_client_lavage (backend v13.60.2 enrichissement direct)
      const nomLavage = cleanCandidate(item.nom_client_lavage);
      if (nomLavage) return `Lavage à pression — ${nomLavage}`;
      // Priorité 2 : autre nom client "propre" (via extractClientName mais pas le legacy "Lavage à pression (...)")
      if (clientName && !clientName.toLowerCase().startsWith('lavage')) {
        return `Lavage à pression — ${clientName}`;
      }
      // Priorité 3 : nb_clients connu → format compact FR-CA
      if (Number.isFinite(nb) && nb > 0) {
        return `Lavage à pression (${nb} client${nb > 1 ? 's' : ''})`;
      }
      // Priorité 4 : legacy client_nom = "Lavage à pression (N clients)" → conservé si présent
      const cn = _s(item.client_nom);
      if (cn.toLowerCase().startsWith('lavage à pression')) return cn;
      return 'Lavage à pression';
    }

    case 'bt_interne': {
      // Format : "Interne — [Catégorie]" ou fallback description tronquée.
      const cat = cleanCandidate(item.categorie_interne_nom);
      const desc = _s(item.description_travaux);
      const titre = cleanCandidate(item.titre_interne);
      if (titre) return `Interne — ${titre}`;
      if (cat) return `Interne — ${cat}`;
      if (desc) return desc.length > 60 ? `Interne — ${desc.slice(0, 60)}…` : `Interne — ${desc}`;
      return 'Interne — sans catégorie';
    }

    case 'bureau': {
      const hd = _s(item.heure_debut);
      const hf = _s(item.heure_fin);
      if (hd && hf) return `Bureau ${hd}–${hf}`;
      // client_nom peut être "Travail de bureau" (my-schedule) ou "08:00–17:00" (my-schedule-month)
      const cn = _s(item.client_nom);
      if (cn) return cn.includes(':') ? `Bureau ${cn}` : cn;
      return 'Bureau';
    }

    case 'conge': {
      // Le backend fournit déjà "Congé (vacances)" → on garde.
      const cn = _s(item.client_nom);
      if (cn && cn.toLowerCase().startsWith('congé')) return cn;
      const type_conge = _s(item.type_conge);
      if (type_conge) return `Congé (${type_conge})`;
      return 'Congé';
    }

    case 'rendez_vous': {
      // Backend fournit déjà "RDV Soumission - Client X" comme client_nom → on garde.
      const cn = _s(item.client_nom);
      if (cn) return cn;
      const titre = _s(item.titre);
      if (titre) return titre;
      return 'Rendez-vous';
    }

    case 'bt_chantier':
    default: {
      if (clientName) return clientName;
      const dossier = cleanCandidate(item.nom_dossier);
      if (dossier) return dossier;
      return 'Chantier';
    }
  }
}

module.exports = {
  formatItemHoraire,
  extractClientName,
  detectType,
  looksLikeTechnicalId,
};

// ES6 default export pour compat React
module.exports.default = formatItemHoraire;
